/**
 * ChatInput Component
 * Premium input field matching mockup design with:
 * - Deep navy styling
 * - Rounded container with border
 * - Large send button
 * - File attachment and mic buttons
 */
import React, { useState, useRef } from "react";
import { Paperclip, Send, Loader2, Mic } from "lucide-react";

interface ChatInputProps {
  onSendMessage: (message: string) => void;
  onUploadFile: (file: File) => void;
  disabled?: boolean;
  sending?: boolean;
}

export const ChatInput: React.FC<ChatInputProps> = ({
  onSendMessage,
  onUploadFile,
  disabled = false,
  sending = false,
}) => {
  const [message, setMessage] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (message.trim() && !disabled && !sending) {
      onSendMessage(message.trim());
      setMessage("");
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onUploadFile(file);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <div className="p-4 bg-gradient-to-t from-[#0a1120] via-[#0a1120] to-transparent">
      <div className="relative bg-[#0f1a30] border border-[#2d4a7c] rounded-2xl p-4 shadow-2xl flex items-end gap-4">
        <div className="flex-1 flex flex-col gap-3">
          {/* Textarea */}
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask anything... (e.g., 'Summarize the last analysis result')"
            disabled={disabled || sending}
            rows={2}
            className="w-full bg-transparent border-none focus:ring-0 focus:outline-none text-slate-200 placeholder-slate-500 resize-none min-h-[60px] max-h-[200px] py-2"
          />
          
          {/* Action buttons row */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled || sending}
              className="p-2 text-slate-400 hover:text-blue-400 hover:bg-blue-500/10 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              title="Attach file"
            >
              <Paperclip size={20} />
            </button>
            <button
              type="button"
              className="p-2 text-slate-400 hover:text-blue-400 hover:bg-blue-500/10 rounded-lg transition-all"
              title="Voice input (coming soon)"
            >
              <Mic size={20} />
            </button>
          </div>
          
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileSelect}
            accept="image/*,video/*"
            className="hidden"
          />
        </div>

        {/* Send button */}
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!message.trim() || disabled || sending}
          className="w-14 h-14 bg-[#1d63ed] hover:bg-[#1652cc] text-white rounded-xl flex items-center justify-center shadow-lg shadow-blue-900/40 transition-all transform active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
          title="Send message"
        >
          {sending ? (
            <Loader2 size={24} className="animate-spin" />
          ) : (
            <Send size={24} className="rotate-[-15deg] ml-1" />
          )}
        </button>
      </div>
    </div>
  );
};
